import { formatDate } from '@angular/common';
import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChild, TemplateRef, ChangeDetectorRef } from '@angular/core';
import { GridOptions } from 'ag-grid-community';
import { Router } from '@angular/router';
import { EquineService } from '../equine.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { QuoteService } from '../../quote.service';
import { EQUINE_REFERRED_INSURANCE_STATUS_ID, shouldShowEquinePremiumAsTbc, normalizeEquineQuotedReferralState } from '../equine-premium-tbc.util';
import { getEquineDisplayPremiumData, requireEquineQuoteId, resolveEquinePreviousPolicyId } from '../equine-utils';
import { SharedService } from 'src/app/services/shared.service';

@Component({
  selector: 'app-equine-premium-summary',
  templateUrl: './equine-premium-summary.component.html',
  styleUrls: ['./equine-premium-summary.component.css', '../../quote.component.css']
})
export class EquinePremiumSummaryComponent implements OnInit, OnChanges {
  @Input() staticData: any;
  @Input() modelObject: any = {};
  @Output() modelObjectUpdated = new EventEmitter<any>();
  @Output() BackToStatementOfFacts = new EventEmitter<void>();

  underwritingNotes = '';
  submitted = false;
  submitting = false;
  saving = false;
  referModalSubmitted = false;
  referModalRefInstance: any;
  manualReferralAttempted = false;
  vulnerabilityMarker = false;
  vulnerabilityMarkerDetails = '';
  referralReasons: any[] = [];
  notesText = '';

  @ViewChild('referModal') referModalRef: TemplateRef<any>;

  constructor(
    private equineService: EquineService,
    private quoteService: QuoteService,
    private sharedService: SharedService,
    private snackBar: MatSnackBar,
    private modalService: NgbModal,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  private syncVulnerabilityMarkerFromModel(): void {
    const detailsRaw =
      this.modelObject?.vulnerability_marker_details ??
      this.modelObject?.vulnerability_details ??
      this.modelObject?.vulnerability_marker_text ??
      '';
    const details = (detailsRaw || '').toString();
    const markerRaw =
      this.modelObject?.vulnerability_marker ??
      this.modelObject?.is_vulnerability_marker ??
      null;
    const markerEnabled =
      markerRaw === true ||
      markerRaw === 1 ||
      markerRaw === '1';

    this.vulnerabilityMarkerDetails = details;
    this.vulnerabilityMarker = markerEnabled || !!details.trim();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.modelObject) {
      this.modelObject = normalizeEquineQuotedReferralState(changes.modelObject.currentValue);
      this.syncVulnerabilityMarkerFromModel();
      this.syncReferralReasonsFromModel();
      const hasModelReasons = (this.referralReasons?.length ?? 0) > 0;
      if ((this.isReferred || hasModelReasons) && this.modelObject?.id) {
        this.getReferralReasons();
      } else if (!hasModelReasons) {
        this.referralReasons = [];
      }

      this.cdr.detectChanges();
    }
  }

  get premiumData(): any {
    return getEquineDisplayPremiumData(this.modelObject);
  }

  /** Quote reference number (e.g. QN-9979) from saved quote. */
  get quoteRefNumber(): string {
    return this.modelObject?.quote_ref_number ?? '';
  }

  private static readonly REFERRED_STATUS_ID = EQUINE_REFERRED_INSURANCE_STATUS_ID;
  private static readonly LABEL_AUTOMATIC_QUOTE = 'AUTOMATIC QUOTE';
  private static readonly LABEL_AUTOMATIC_REFERRAL = 'AUTOMATIC REFERRAL';
  private static readonly LABEL_AUTOMATIC_MTA = 'AUTOMATIC REFERRAL FOR MTA';
  private static readonly LABEL_MANUAL_REFERRAL = 'MANUAL REFERRAL';
  
  private buildAdditionalNote(note: string): string {
    const trimmed = (note || '').trim();
    if (!trimmed) return '';
    return trimmed;
  }

  private buildCombinedNote(label: string, additionalNote: string): string {
    const additional = this.buildAdditionalNote(additionalNote);
    return additional ? `${label}\n${additional}` : label;
  }

  get hasAdditionalNotes(): boolean {
    return !!(this.underwritingNotes || '').trim();
  }

  // Automatic label for summary
  get automaticLabelForSummary(): string {
    if(this.isReferred){ 
      return EquinePremiumSummaryComponent.LABEL_AUTOMATIC_REFERRAL;
    }
    
    if(this.modelObject.type === 3){
      return EquinePremiumSummaryComponent.LABEL_AUTOMATIC_MTA;
    }

    return EquinePremiumSummaryComponent.LABEL_AUTOMATIC_QUOTE;
  }

  private saveNotesSequence(
    quoteId: any,
    notes: string[],
    onSuccess: () => void,
    onError: () => void,
    idx = 0
  ): void {
    if (!quoteId || idx >= notes.length) {
      onSuccess();
      return;
    }
    const note = (notes[idx] || '').trim();
    if (!note) {
      this.saveNotesSequence(quoteId, notes, onSuccess, onError, idx + 1);
      return;
    }
    this.quoteService.saveNotes({
      quote_id: quoteId,
      notes: note,
      is_important: 0
    }).subscribe(
      () => this.saveNotesSequence(quoteId, notes, onSuccess, onError, idx + 1),
      () => onError()
    );
  }

  get isReferred(): boolean {
    return this.modelObject?.insurance_status_id === EquinePremiumSummaryComponent.REFERRED_STATUS_ID;
  }

  get showReferralReasons(): boolean {
    return (this.referralReasons?.length ?? 0) > 0;
  }

  private formatSofReferralReason(item: any): string {
    const parts = [item?.question, item?.response_text, item?.trigger]
      .map((part) => (part || '').trim())
      .filter(Boolean);
    return parts.join(' — ') || 'Referral';
  }

  private syncReferralReasonsFromModel(): void {
    const sofReasons = Array.isArray(this.modelObject?.referred_reasons)
      ? this.modelObject.referred_reasons
      : [];
    if (sofReasons.length) {
      this.referralReasons = sofReasons.map((item: any) => ({
        reason: this.formatSofReferralReason(item),
        latest_date: this.modelObject?.updated_at ?? null,
      }));
      return;
    }
    const manual = (this.modelObject?.manually_referred_reason || '').trim();
    if (manual && this.isReferred) {
      this.referralReasons = [{
        reason: manual,
        latest_date: this.modelObject?.updated_at ?? null,
      }];
    }
  }

  get showPremiumAsTbc(): boolean {
    return shouldShowEquinePremiumAsTbc(this.modelObject, this.staticData);
  }

  ngOnInit(): void {
    this.syncVulnerabilityMarkerFromModel();
  }

  backToStatementOfFacts(): void {
    this.BackToStatementOfFacts.emit();
  }
  
  public getReferralReasons(): void {
    this.equineService
      .getQuoteReferralReasons(this.modelObject?.id)
      .subscribe({
        next: (response: any) => {
          this.referralReasons = [{
            reason: response.result.concatenated_reasons,
            latest_date: response.result.latest_date
          }];
        },
        error: () => {
          this.referralReasons = null;
        }
      });
  }

  gridOptions: GridOptions = {
    onGridReady: (params) => {
      params.api.sizeColumnsToFit();
    }
  };

  referralReasonNotes = [
    {
      headerName: 'Referral Reason',
      field: 'reason',
      sortable: true,
      width: 500,
      cellRenderer: function(params) {
        return `<a data-action-type="view">${params.data.reason || ''}</a>`;
      }
    },
    {
      headerName: 'Created At',
      field: 'created_at',
      sortable: true,
      width: 200,
      comparator: this.sharedService.agGridDteSortingComparator,
      cellRenderer: function(params) {
        return formatDate(params.data.latest_date, 'dd-MM-yyyy hh:mm a', 'en-US');
      }
    }
  ];

  onGridRowClicked(e: any, notesViewModel: TemplateRef<any>): void {
    const actionType = e?.event?.target?.getAttribute('data-action-type');
    if (actionType !== 'view') return;
    this.notesText = e?.data?.reason || '';
    this.modalService.open(notesViewModel, { centered: true });
  }

  openReferModal(): void {
    this.manualReferralAttempted = true;
    if (!this.hasAdditionalNotes) {
      this.snackBar.open('Please add additional notes before manually referring.', 'Close', {
        duration: 3000,
        panelClass: 'text-danger'
      });
      return;
    }
    this.referModalSubmitted = false;
    if (this.referModalRef) {
      this.referModalRefInstance = this.modalService.open(this.referModalRef, { centered: true });
    }
  }

  dismissReferModal(): void {
    if (this.referModalRefInstance) {
      this.referModalRefInstance.dismiss();
      this.referModalRefInstance = null;
    }
  }

  submitManuallyRefer(): void {
    this.referModalSubmitted = true;
    this.manualReferralAttempted = true;
    const additionalNotes = this.buildAdditionalNote(this.underwritingNotes);
    if (!additionalNotes) {
      this.snackBar.open('Please add additional notes before manually referring.', 'Close', {
        duration: 3000,
        panelClass: 'text-danger'
      });
      return;
    }
    const quoteId = requireEquineQuoteId(this.modelObject);
    if (!quoteId) {
      this.snackBar.open('Quote must be saved before referring.', 'Close', {
        duration: 3000,
        panelClass: 'text-danger'
      });
      return;
    }
    const payload = {
      ...this.modelObject,
      id: quoteId,
      transaction_type: this.modelObject.type,
      is_manually_referred: 1,
      manually_referred_reason: additionalNotes,
      is_send_email: true,
    };
    const prevPolicyIdRefer = this.modelObject.previous_policy_id;
    const txnTypeRefer = this.modelObject.type || this.modelObject.transaction_type;
    const origTxTypeRefer = this.modelObject.original_transaction_type;
    this.equineService.saveQuote(payload).subscribe(
      (response: any) => {
        if (response.success) {
          if (this.referModalRefInstance) {
            this.referModalRefInstance.close();
            this.referModalRefInstance = null;
          }
          this.modelObject = normalizeEquineQuotedReferralState(response.result);
          if (prevPolicyIdRefer) this.modelObject.previous_policy_id = prevPolicyIdRefer;
          if (txnTypeRefer) this.modelObject.type = txnTypeRefer;
          if (origTxTypeRefer) this.modelObject.original_transaction_type = origTxTypeRefer;
          const quoteId = response.result?.id || this.modelObject?.id;
          if (!quoteId) {
            this.modelObjectUpdated.emit(this.modelObject);
            this.snackBar.open('Quote referred to Underwriter successfully.', 'Close', { duration: 5000 });
            return;
          }
          this.quoteService.updateAnyQuoteStatus({
            quote_id: quoteId,
            insurance_status_id: EquinePremiumSummaryComponent.REFERRED_STATUS_ID
          }).subscribe(
            (statusRes: any) => {
              this.modelObject.insurance_status_id = EquinePremiumSummaryComponent.REFERRED_STATUS_ID;
              this.modelObject.is_manually_referred = 1;
              this.modelObject.manually_referred_reason = additionalNotes;
              this.modelObjectUpdated.emit(this.modelObject);
              this.saveNotesSequence(
                quoteId,
                [this.buildCombinedNote(EquinePremiumSummaryComponent.LABEL_MANUAL_REFERRAL, additionalNotes)],
                () => {
                  this.snackBar.open('Quote referred to Underwriter successfully.', 'Close', { duration: 5000 });
                  this.router.navigate(['/equine', 'quote-details', quoteId]);
                },
                () => {
                  this.snackBar.open('Quote referred, but failed to save notes.', 'Close', {
                    duration: 5000,
                    panelClass: 'text-danger'
                  });
                  this.router.navigate(['/equine', 'quote-details', quoteId]);
                }
              );
            },
            () => {
              this.modelObjectUpdated.emit(this.modelObject);
              this.snackBar.open('Quote referral saved, but status update failed.', 'Close', {
                duration: 5000,
                panelClass: 'text-danger'
              });
            }
          );
        } else {
          this.snackBar.open(response.message || 'Failed to refer quote.', 'Close', {
            duration: 5000,
            panelClass: 'text-danger'
          });
        }
      },
      () => {
        this.snackBar.open('Error referring quote. Please try again.', 'Close', {
          duration: 5000,
          panelClass: 'text-danger'
        });
      }
    );
  }

  public saveAndViewQuote(): void {
    const quoteId = requireEquineQuoteId(this.modelObject);
    if (!quoteId) {
      this.snackBar.open('Quote must be saved first.', 'Close', {
        duration: 3000,
        panelClass: 'text-danger'
      });
      return;
    }
    this.saving = true;
    this.submitted = true;
    const vulnerabilityDetails = (this.vulnerabilityMarkerDetails || '').trim();
    if (this.vulnerabilityMarker && !vulnerabilityDetails) {
      this.saving = false;
      this.snackBar.open('Please provide vulnerability marker details.', 'Close', {
        duration: 3000,
        panelClass: 'text-danger'
      });
      return;
    }
    const notes = (this.underwritingNotes || '').trim();
    const automaticLabel = this.automaticLabelForSummary;
    const m = this.modelObject;
    const mergedHd: any = { ...(m.horse_details || {}), ...(m.horse_Details || {}) };
    const ownRaw =
      mergedHd.ownership_status_id ??
      (mergedHd.ownership_status != null && typeof mergedHd.ownership_status === 'object'
        ? mergedHd.ownership_status.id
        : mergedHd.ownership_status);
    const horse_details =
      ownRaw != null && ownRaw !== ''
        ? { ...mergedHd, ownership_status_id: ownRaw, ownership_status: ownRaw }
        : { ...mergedHd };
    const { horse_details: _dropHd, horse_Details: _dropHDD, ...modelRest } = m;
    const isMta = Number(m.type ?? m.transaction_type) === 3;
    const hasManualReason = !!(m.manually_referred_reason || '').trim();
    const payload = {
      ...modelRest,
      id: quoteId,
      horse_details,
      is_quoted: this.isReferred ? 0 : 1,
      is_manually_referred: isMta
        ? (hasManualReason ? (m.is_manually_referred ?? 0) : 0)
        : (this.isReferred ? 1 : (m.is_manually_referred ?? 0)),
      transaction_type: m.type,
      underwriting_notes: notes ? `${automaticLabel}\n${notes}` : automaticLabel,
      previous_policy_id: resolveEquinePreviousPolicyId(m),
      vulnerability_marker_details: this.vulnerabilityMarker ? vulnerabilityDetails : null,
      should_create_note: true,
      has_form_changed: false,
      is_send_email: true,
    };

    const prevPolicyIdSave = this.modelObject.previous_policy_id;
    const txnTypeSave = this.modelObject.type || this.modelObject.transaction_type;
    const origTxTypeSave = this.modelObject.original_transaction_type;
    this.equineService.saveQuote(payload).subscribe(
      (response: any) => {
        this.saving = false;
        if (response.success) {
          this.modelObject = normalizeEquineQuotedReferralState(response.result);
          if (prevPolicyIdSave) this.modelObject.previous_policy_id = prevPolicyIdSave;
          if (txnTypeSave) this.modelObject.type = txnTypeSave;
          if (origTxTypeSave) this.modelObject.original_transaction_type = origTxTypeSave;
          this.modelObjectUpdated.emit(this.modelObject);
          this.snackBar.open('Quote saved successfully.', 'Close', { duration: 3000 });
          const savedQuoteId = response.result?.id || this.modelObject?.id;
          if (savedQuoteId) {
            this.router.navigate(['/equine', 'quote-details', savedQuoteId]);
          }
        } else {
          this.snackBar.open(response.message || 'Failed to save quote.', 'Close', {
            duration: 5000,
            panelClass: 'text-danger'
          });
        }
      },
      () => {
        this.saving = false;
        this.snackBar.open('Error saving quote. Please try again.', 'Close', {
          duration: 5000,
          panelClass: 'text-danger'
        });
      }
    );
  }
}
